import { create } from "zustand"

import type { CustomerOrderSearchParams } from "../schemas"

import {
  type CustomerLocationCombination,
  CustomerOrderDateRangeType,
  GetCustomerOrderListDocument,
  type GetCustomerOrderListQuery,
} from "@/graphql/codegen/graphql"
import { createSearchStore } from "@/stores/createSearchStore"

const deduplicateCustomerOrders = (
  newData: CustomerLocationCombination[],
  existingData: CustomerLocationCombination[]
): CustomerLocationCombination[] => {
  const uniqueIds = new Set(existingData.map((combination) => combination.combinationId))
  return [...existingData, ...newData.filter((combination) => !uniqueIds.has(combination.combinationId))]
}

export const useCustomerOrderSearchStore = create(
  createSearchStore<CustomerLocationCombination, CustomerOrderSearchParams, GetCustomerOrderListQuery>({
    documentNode: GetCustomerOrderListDocument,
    deduplicateData: deduplicateCustomerOrders,

    getQueryInput: (filters: CustomerOrderSearchParams, offset: number, limit: number) => {
      const { query, field, order, statuses, dateFrom, dateTo, dateRangeType } = filters
      return {
        filter: {
          query,
          statuses,
          dateFrom,
          dateTo,
          dateRangeType: dateRangeType ?? (dateFrom || dateTo ? CustomerOrderDateRangeType.DeliveryDate : undefined),
        },
        sort: field && order ? { field, order } : undefined,
        offset,
        limit,
      }
    },

    getQueryVariables: (queryInput) => ({ input: queryInput }),

    extractDataFromResponse: (data) => ({
      __typename: data.customerOrder.list.__typename,
      data:
        data.customerOrder.list.__typename === "CustomerOrderListSuccess"
          ? (data.customerOrder.list.combinations as CustomerLocationCombination[])
          : [],
      error:
        data.customerOrder.list.__typename === "CustomerOrderListFailure" ? data.customerOrder.list.error : undefined,
    }),

    hasMoreData: (response, state) => response.data.length === state.limit,
  })
)
